import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-background">
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-900/20 via-background to-background">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl font-bold text-foreground mb-6">Privacy Policy</h1>

          <div className="prose prose-lg max-w-none space-y-8 text-muted-foreground">
            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">1. Introduction</h2>
              <p>
                ALM Group of Companies ("we," "our," or "us") is committed to protecting your privacy. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
                when you visit our website, use our services, or engage with any of our four divisions: 
                ALM Trading LLC, ALM Consultants LLC, ALM IT Services, and ALM Software Services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">2. Information We Collect</h2>
              <p className="mb-4">We may collect information about you in various ways, including:</p>
              
              <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Personal Information</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Name, email address, phone number, and mailing address</li>
                <li>Company name and business information</li>
                <li>Job title and professional details</li>
                <li>Contact preferences and communication history</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Financial Information</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Payment card details for processing transactions</li>
                <li>Billing address and payment history</li>
                <li>Bank account information (for wire transfers)</li>
                <li>Tax identification numbers (when required)</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Service-Specific Information</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Trading Services:</strong> Shipping details, customs information, import/export documentation</li>
                <li><strong>Consulting Services:</strong> Business requirements, financial data, operational information</li>
                <li><strong>IT Services:</strong> Network configurations, system specifications, security requirements</li>
                <li><strong>Software Services:</strong> Project requirements, technical specifications, user data</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Usage Data</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>IP address, browser type, and device information</li>
                <li>Pages visited and time spent on our website</li>
                <li>Referring URLs and exit pages</li>
                <li>Operating system and platform details</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Cookies and Tracking Technologies</h3>
              <p>
                We use cookies, web beacons, and similar tracking technologies to enhance user experience, 
                analyze site traffic, and personalize content.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">3. How We Use Your Information</h2>
              <p className="mb-4">We use the collected information to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide, operate, and maintain our services across all divisions</li>
                <li>Process transactions and send related information (invoices, receipts, notifications)</li>
                <li>Manage your account and provide customer support</li>
                <li>Send administrative information, updates, and security alerts</li>
                <li>Respond to inquiries and fulfill your requests</li>
                <li>Improve our website, services, and user experience</li>
                <li>Conduct research and analysis to enhance our offerings</li>
                <li>Send marketing communications (with your consent)</li>
                <li>Monitor and analyze usage patterns and trends</li>
                <li>Detect, prevent, and address fraud or security issues</li>
                <li>Comply with legal obligations and enforce our Terms & Conditions</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">4. How We Share Your Information</h2>
              <p className="mb-4">We may share your information in the following situations:</p>
              
              <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Service Providers</h3>
              <p>
                Third-party vendors who perform services on our behalf, including payment processors, 
                shipping partners, cloud hosting providers, analytics services, and customer support platforms.
              </p>

              <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Business Transfers</h3>
              <p>
                In connection with mergers, acquisitions, reorganizations, asset sales, or bankruptcy proceedings.
              </p>

              <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Legal Requirements</h3>
              <p>
                When required by law, court order, or government regulation, or to protect our rights, 
                property, or safety, or that of others.
              </p>

              <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">With Your Consent</h3>
              <p>
                When you have given us explicit permission to share your information for specific purposes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">5. Data Security</h2>
              <p>
                ALM Group of Companies implements appropriate technical and organizational security measures 
                to protect your personal information. However, no method of electronic transmission or storage 
                is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">6. Your Privacy Rights</h2>
              <p className="mb-4">Depending on your location, you may have the following rights:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Access and receive a copy of your personal data</li>
                <li>Correct inaccurate or incomplete data</li>
                <li>Request deletion of your personal data</li>
                <li>Object to or restrict processing of your data</li>
                <li>Data portability</li>
                <li>Withdraw consent at any time</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">7. Children's Privacy</h2>
              <p>
                Our services are not intended for children under 13 years of age. We do not knowingly collect 
                personal information from children under 13.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">8. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting 
                the new Privacy Policy on this page and updating the "Last updated" date.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-foreground mb-4">9. Contact Us</h2>
              <p>
                If you have questions or concerns about this Privacy Policy, please contact ALM Group of Companies:
              </p>
              <div className="mt-4 p-6 bg-card border border-border rounded-lg">
                <p><strong>ALM Group of Companies</strong></p>
                <p>Email: info@almgroupofcompanies.com</p>
                <p>Phone: +1 (555) 123-4567</p>
                <p>Address: 650 California St, San Francisco, CA 94108</p>
              </div>
            </section>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
